import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, Save, Send, EyeOff, Trash2, Sparkles, Loader2, ImagePlus, X, ExternalLink, Eye, Pencil } from "lucide-react";
import { toast } from "sonner";
import DOMPurify from "dompurify";
import api, { apiError } from "../../../lib/api";
import { useAuth } from "../../../context/AuthContext";
import { Label } from "../../../components/common";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Textarea } from "../../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../../../components/ui/alert-dialog";
import RichEditor from "../../../components/RichEditor";
import { postStatus } from "./PlatformBlog";

const EMPTY = { title: "", slug: "", category: "", summary: "", seo_title: "", body_html: "", cover_image_id: null, cover_url: null, author_name: "", published_at: "" };
const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 90);
const toLocalInput = (iso) => (iso ? new Date(new Date(iso).getTime() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16) : "");
const readFile = (file) => new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file); });

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="text-sm font-bold text-slate-800">{label}</span>
      {hint && <span className="ml-2 text-xs text-slate-400">{hint}</span>}
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

export default function PlatformBlogEditor() {
  const { id } = useParams();
  const isNew = id === "new";
  const navigate = useNavigate();
  const { user } = useAuth();
  const canPublish = user.role === "master_admin";
  const [p, setP] = useState(isNew ? { ...EMPTY, author_name: user.name || "" } : null);
  const [cats, setCats] = useState([]);
  const [slugTouched, setSlugTouched] = useState(!isNew);
  const [busy, setBusy] = useState("");
  const [preview, setPreview] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [delOpen, setDelOpen] = useState(false);

  useEffect(() => {
    api.get("/platform/blog").then((r) => setCats(r.data.categories)).catch(() => {});
    if (!isNew) api.get(`/platform/blog/${id}`).then((r) => setP({ ...EMPTY, ...r.data, published_at: toLocalInput(r.data.published_at) })).catch((e) => toast.error(apiError(e)));
  }, [id, isNew]);

  if (!p) return <div className="text-slate-400">Loading…</div>;
  const set = (k) => (v) => setP((cur) => ({ ...cur, [k]: v, ...(k === "title" && !slugTouched ? { slug: slugify(v) } : {}) }));
  const payload = () => ({ ...p, published_at: p.published_at ? new Date(p.published_at).toISOString() : null });

  const save = async (quiet) => {
    if (!p.title.trim()) { toast.error("Add a title first"); return null; }
    setBusy("save");
    try {
      const { data } = isNew ? await api.post("/platform/blog", payload()) : await api.put(`/platform/blog/${id}`, payload());
      setP({ ...EMPTY, ...data, published_at: toLocalInput(data.published_at) });
      if (!quiet) toast.success("Saved");
      if (isNew) navigate(`/app/platform/blog/${data.id}`, { replace: true });
      return data;
    } catch (e) { toast.error(apiError(e)); return null; } finally { setBusy(""); }
  };
  const act = async (kind) => {
    const saved = kind === "publish" ? await save(true) : p;
    if (!saved) return;
    setBusy(kind);
    try {
      await api.post(`/platform/blog/${saved.id}/${kind}`);
      const { data } = await api.get(`/platform/blog/${saved.id}`);
      setP({ ...EMPTY, ...data, published_at: toLocalInput(data.published_at) });
      toast.success(kind === "publish" ? "Published" : "Moved back to drafts");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };
  const remove = async () => {
    try { await api.delete(`/platform/blog/${id}`); toast.success("Article deleted"); navigate("/app/platform/blog"); } catch (e) { toast.error(apiError(e)); }
  };
  const upload = async (file) => {
    try { const { data } = await api.post("/platform/blog/images", { data_url: await readFile(file) }); return data; } catch (e) { toast.error(apiError(e)); return null; }
  };
  const uploadCover = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    setBusy("cover");
    const img = await upload(f);
    setBusy("");
    if (img) setP((cur) => ({ ...cur, cover_image_id: img.id, cover_url: img.url }));
  };
  const status = isNew ? "draft" : postStatus(p);
  const locked = p.status === "published" && !canPublish;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <Link to="/app/platform/blog" className="inline-flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-slate-800" data-testid="editor-back"><ArrowLeft className="h-4 w-4" />All articles</Link>
          <h1 className="mt-1 font-head text-2xl font-bold text-slate-900">{isNew ? "New article" : "Edit article"} <span className="ml-2 rounded-full bg-slate-100 px-2 py-0.5 align-middle text-xs font-bold capitalize text-slate-600" data-testid="editor-status">{status}</span></h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => setAiOpen(true)} disabled={locked} data-testid="editor-ai"><Sparkles className="mr-1 h-4 w-4 text-indigo-600" />AI draft</Button>
          <Button variant="outline" onClick={() => setPreview((v) => !v)} data-testid="editor-preview">{preview ? <><Pencil className="mr-1 h-4 w-4" />Edit</> : <><Eye className="mr-1 h-4 w-4" />Preview</>}</Button>
          {p.status === "published" && <a href={`/insights/${p.slug}`} target="_blank" rel="noreferrer"><Button variant="outline" data-testid="editor-view-live"><ExternalLink className="mr-1 h-4 w-4" />View live</Button></a>}
          <Button variant="outline" onClick={() => save(false)} disabled={!!busy || locked} data-testid="editor-save">{busy === "save" ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Save className="mr-1 h-4 w-4" />}Save{p.status === "published" ? "" : " draft"}</Button>
          {canPublish && p.status !== "published" && <Button onClick={() => act("publish")} disabled={!!busy} className="bg-[#4338CA] hover:bg-[#3730A3]" data-testid="editor-publish">{busy === "publish" ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Send className="mr-1 h-4 w-4" />}Publish</Button>}
          {canPublish && p.status === "published" && <Button variant="outline" onClick={() => act("unpublish")} disabled={!!busy} data-testid="editor-unpublish"><EyeOff className="mr-1 h-4 w-4" />Unpublish</Button>}
          {canPublish && !isNew && <Button variant="outline" className="text-red-600" onClick={() => setDelOpen(true)} data-testid="editor-delete"><Trash2 className="h-4 w-4" /></Button>}
        </div>
      </div>
      {locked && <p className="rounded-lg bg-amber-50 p-3 text-sm font-semibold text-amber-800">This article is live. Only the Master admin can edit it.</p>}

      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        <div className="space-y-4">
          <Input value={p.title} onChange={(e) => set("title")(e.target.value)} placeholder="Article title" className="h-14 font-head text-2xl font-bold md:text-2xl" data-testid="editor-title" />
          {preview ? (
            <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="editor-preview-pane">
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-indigo-600">{p.category || cats[0]}</p>
              <h2 className="mt-2 font-head text-2xl font-bold text-slate-900 sm:text-3xl">{p.title || "Untitled"}</h2>
              <p className="mt-2 text-slate-600">{p.summary}</p>
              {p.cover_url && <img src={p.cover_url} alt="" className="mt-5 aspect-[16/9] w-full rounded-xl object-cover" />}
              <div className="sv-prose mt-6" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(p.body_html) }} />
            </div>
          ) : <RichEditor value={p.body_html} onChange={set("body_html")} onUploadImage={async (f) => (await upload(f))?.url} testId="editor-body" />}
        </div>
        <Sidebar p={p} set={set} cats={cats} setSlugTouched={setSlugTouched} uploadCover={uploadCover} busy={busy} setP={setP} />
      </div>

      <AiDraftDialog open={aiOpen} onClose={() => setAiOpen(false)} title={p.title} hasBody={!!p.body_html && p.body_html !== "<p></p>"}
        onDone={(r, t) => setP((cur) => ({ ...cur, title: t, slug: slugTouched ? cur.slug : slugify(t), summary: r.summary, seo_title: r.seo_title, body_html: r.body_html }))} />
      <AlertDialog open={delOpen} onOpenChange={setDelOpen}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete this article?</AlertDialogTitle>
            <AlertDialogDescription>This permanently removes "{p.title}" from the blog and the sitemap.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={remove} className="bg-red-600 hover:bg-red-700" data-testid="editor-delete-confirm">Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Sidebar({ p, set, cats, setSlugTouched, uploadCover, busy, setP }) {
  return (
    <aside className="space-y-4 rounded-xl border border-slate-200 bg-white p-5 lg:sticky lg:top-20 lg:self-start" data-testid="editor-sidebar">
      <Label>Article settings</Label>
      <Field label="Cover image" hint="16:9 works best">
        {p.cover_url ? (
          <div className="relative"><img src={p.cover_url} alt="Cover" className="aspect-[16/9] w-full rounded-lg object-cover" data-testid="editor-cover-img" />
            <button onClick={() => setP((c) => ({ ...c, cover_image_id: null, cover_url: null }))} className="absolute right-2 top-2 rounded-full bg-white/90 p-1 text-slate-600 hover:text-red-600" aria-label="Remove cover" data-testid="editor-cover-remove"><X className="h-4 w-4" /></button></div>
        ) : (
          <span className="flex aspect-[16/9] cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-slate-200 text-sm text-slate-400 hover:border-indigo-300">
            {busy === "cover" ? <Loader2 className="h-5 w-5 animate-spin" /> : <ImagePlus className="h-5 w-5" />}Upload image
            <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={uploadCover} data-testid="editor-cover-input" />
          </span>
        )}
      </Field>
      <Field label="Category">
        <Select value={p.category || cats[0] || ""} onValueChange={set("category")}>
          <SelectTrigger data-testid="editor-category"><SelectValue /></SelectTrigger>
          <SelectContent>{cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
      </Field>
      <Field label="Summary" hint={`${(p.summary || "").length}/155 · shown in Google`}>
        <Textarea rows={3} value={p.summary} onChange={(e) => set("summary")(e.target.value)} placeholder="One or two sentences that sell the article" data-testid="editor-summary" />
      </Field>
      <Field label="Search title" hint={`${(p.seo_title || "").length}/60 · optional`}>
        <Input value={p.seo_title || ""} onChange={(e) => set("seo_title")(e.target.value)} placeholder="Defaults to the title" data-testid="editor-seo-title" />
      </Field>
      <Field label="URL" hint="servantage.co.za/insights/…">
        <Input value={p.slug} onChange={(e) => { setSlugTouched(true); set("slug")(e.target.value); }} data-testid="editor-slug" />
      </Field>
      <Field label="Author"><Input value={p.author_name} onChange={(e) => set("author_name")(e.target.value)} data-testid="editor-author" /></Field>
      <Field label="Publish date" hint="optional · future date schedules it">
        <Input type="datetime-local" value={p.published_at || ""} onChange={(e) => set("published_at")(e.target.value)} data-testid="editor-publish-date" />
      </Field>
    </aside>
  );
}

function AiDraftDialog({ open, onClose, title, hasBody, onDone }) {
  const [t, setT] = useState(title);
  const [kw, setKw] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setT(title); }, [open, title]);
  const run = async () => {
    if (!t.trim()) { toast.error("Add a title"); return; }
    if (hasBody && !window.confirm("Replace the current article body with the AI draft?")) return;
    setBusy(true);
    try { const { data } = await api.post("/platform/blog/ai-draft", { title: t, keywords: kw }); onDone(data, t); toast.success("Draft ready. Review and edit before publishing"); onClose(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent data-testid="ai-draft-dialog">
        <DialogHeader><DialogTitle>AI draft from a title</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Field label="Title"><Input value={t} onChange={(e) => setT(e.target.value)} data-testid="ai-draft-title" /></Field>
          <Field label="Search phrases" hint="optional, comma separated"><Textarea rows={2} value={kw} onChange={(e) => setKw(e.target.value)} placeholder="e.g. employee QR code service rating" data-testid="ai-draft-keywords" /></Field>
          <p className="text-xs text-slate-400">Writes a summary, search title and a 700–1000 word body. It won't invent statistics or quotes, but always fact-check before publishing.</p>
        </div>
        <DialogFooter><Button onClick={run} disabled={busy} className="bg-[#4338CA] hover:bg-[#3730A3]" data-testid="ai-draft-run">{busy ? <><Loader2 className="mr-1 h-4 w-4 animate-spin" />Writing… (up to a minute)</> : <><Sparkles className="mr-1 h-4 w-4" />Write draft</>}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
